import { DateTime } from "luxon"
import { DateTimeFactory } from "../../../../../../localization/useDateTime"
import { MeetingDetailsOrder } from "../../../../../../nonAdminUser/reports/home/meetingServiceApi"
import { GroupBy, MeetingServiceOrder, DeliveryType, getDeliveryTypeValue } from "../../meetingServiceTypes"

type TimeSlotsByDate = Record<
    string, // dateKey: "yyyy-LL-dd"
    Record<string, MeetingDetailsOrder[]> // timeSlotKey → orders[]
>

export type GroupedByTime = TimeSlotsByDate

export type GroupedByLocation = Record<
    string, // dateKey: "yyyy-LL-dd"
    Record<
        string, // locationName
        Record<string, MeetingDetailsOrder[]> // timeSlotKey → orders[]
    >
>

const normalizeBackendTime = (time: string): string => (time.includes("T") ? time : time.replace(" ", "T"))

export const getDateKey = (dt: DateTime): string => dt.toFormat("yyyy-LL-dd")

const getTimeSlotKeyFromDt = (dt: DateTime): string => dt.startOf("hour").toISO()!

export const getTimeSlotLabel = (slotKey: string, dateTimeFactory: DateTimeFactory): string => {
    const start = dateTimeFactory.fromISO(slotKey)
    if (!start.isValid) return slotKey

    const end = start.plus({ hours: 1 })
    return `${dateTimeFactory.toHourMinute(start)} - ${dateTimeFactory.toHourMinute(end)}`
}

const resolveDeliveryDateTime = (order: MeetingDetailsOrder | MeetingServiceOrder): string | undefined => {
    const anyOrder = order as any

    const fromDeliveries: string | undefined = anyOrder.deliveries?.[0]?.deliveryTime
    if (fromDeliveries) {
        return normalizeBackendTime(fromDeliveries)
    }

    const fromDetails: string | undefined = anyOrder.deliveryDetails?.time
    if (fromDetails) {
        return normalizeBackendTime(fromDetails)
    }

    return undefined
}

export const groupOrdersByDateAndType = (
    orders: MeetingDetailsOrder[] | MeetingServiceOrder[],
    groupBy: GroupBy,
    dateTimeFactory: DateTimeFactory,
): GroupedByTime | GroupedByLocation => {
    if (groupBy === "time") {
        // date → timeSlot → orders[]
        const result: GroupedByTime = {}

        for (const order of orders) {
            const dateTimeString = resolveDeliveryDateTime(order)
            if (!dateTimeString) {
                console.warn("Missing delivery time for order", order)
                continue
            }

            const dt = dateTimeFactory.fromISO(dateTimeString)

            if (!dt.isValid) {
                console.warn("Invalid delivery time:", order.deliveryDetails.time, dt.invalidExplanation)
                continue
            }

            const dateKey = getDateKey(dt)
            const timeSlotKey = getTimeSlotKeyFromDt(dt)

            if (!result[dateKey]) result[dateKey] = {}
            if (!result[dateKey][timeSlotKey]) result[dateKey][timeSlotKey] = []

            result[dateKey][timeSlotKey].push(order as MeetingDetailsOrder)
        }

        return result
    }

    // groupBy === "location"
    // date → location → timeSlot → orders[]
    const result: GroupedByLocation = {}

    for (const order of orders) {
        const dateTimeString = resolveDeliveryDateTime(order)
        if (!dateTimeString) {
            console.warn("Missing delivery time for order", order)
            continue
        }

        const dt = dateTimeFactory.fromISO(dateTimeString)

        if (!dt.isValid) {
            console.warn("Invalid delivery time:", order.deliveryDetails.time, dt.invalidExplanation)
            continue
        }

        const locationNameFromDetails = order.deliveryDetails?.location?.name
        const deliveryTypeValue = getDeliveryTypeValue(order.deliveryDetails.type)

        // For "Eating in canteen" and "Picked up", use delivery type as location name
        let locationName: string

        if (deliveryTypeValue === DeliveryType.EATING_IN_CANTEEN || deliveryTypeValue === DeliveryType.PICK_UP) {
            locationName = deliveryTypeValue
        } else {
            locationName = locationNameFromDetails || "Unknown location"
        }

        const dateKey = getDateKey(dt)
        const timeSlotKey = getTimeSlotKeyFromDt(dt)

        if (!result[dateKey]) result[dateKey] = {}
        if (!result[dateKey][locationName]) result[dateKey][locationName] = {}
        if (!result[dateKey][locationName][timeSlotKey]) result[dateKey][locationName][timeSlotKey] = []

        result[dateKey][locationName][timeSlotKey].push(order as MeetingDetailsOrder)
    }

    return result
}
